import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import PremiumModal from "../components/premium/PremiumModal";
import { DIP_CONFIG } from "../config/dip";
import { useAlgoliaChat } from "../hooks/useAlgoliaChat";
import { SearchBar, SummaryBar, FilterPanel } from "../components/search";
import { FormationCard, DipDetailPanel } from "../components/dip";
import JobSuggestions from "../components/job/JobSuggestions";

export default function Dip() {
  const { user, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPremiumModal, setShowPremiumModal] = useState(!user?.is_premium);
  const cfg = DIP_CONFIG;

  const {
    inputValue, setInputValue,
    results, facets, hasSearched, thinking, filterLoading, loadingMore,
    summary, filterState, nbHits, showFilters, setShowFilters,
    selectedHit, setSelectedHit, recording, transcribing, activeCount,
    inputRef, sentinelRef,
    handleSubmit, handleReset, handleFilterToggle, handleFilterReset,
    toggleRecording, runSearch,
  } = useAlgoliaChat({
    algoliaIndex: cfg.algoliaIndex,
    assistantName: 'dip',
    filterDimensions: cfg.filterDimensions,
    filtersData: null, // DIP utilise dim.values, pas FiltersContext
    geo: cfg.geo,
  });

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-gray-900">

      {/* HEADER */}
      <div className="px-5 py-3 border-b border-gray-800 shrink-0 flex items-center gap-3">
        <div className="ml-12 md:ml-0 w-9 h-9 rounded-xl bg-teal-500 flex items-center justify-center text-base font-bold text-white shrink-0">
          {cfg.header.icon}
        </div>
        <div className="min-w-0">
          <h1 className="text-sm font-bold text-white leading-tight">{cfg.header.title}</h1>
          <p className="text-xs text-gray-500">{cfg.header.subtitle}</p>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden relative">

        {/* MAIN */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">

          {/* CONTRÔLES STICKY */}
          <div className="px-5 pt-4 pb-0 shrink-0">
            <SearchBar
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
              onReset={handleReset}
              hasSearched={hasSearched}
              recording={recording}
              transcribing={transcribing}
              thinking={thinking}
              onToggleRecording={toggleRecording}
              inputRef={inputRef}
              placeholder={hasSearched ? "Affine ta recherche…" : "Décris les formations que tu recherches…"}
            />
            {!thinking && (
              <SummaryBar
                summary={summary}
                nbHits={nbHits}
                resultLabel={cfg.resultLabel}
                filterLoading={filterLoading}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters((s) => !s)}
                activeCount={activeCount}
              />
            )}
          </div>

          {/* ZONE SCROLLABLE */}
          <div className="flex-1 overflow-y-auto job-scrollbar px-5 pb-6">

            {/* Thinking */}
            {thinking && (
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl mb-5 mt-4 text-sm text-gray-400">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
                {cfg.header.title} analyse ta demande…
              </div>
            )}

            {/* Grille de résultats */}
            {hasSearched && !thinking && (
              results.length === 0 && !filterLoading ? (
                <div className="text-center py-16">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="text-sm font-semibold text-gray-300 mb-1.5">Aucune {cfg.resultLabel.singular} trouvée</p>
                  <p className="text-xs text-gray-500">Essaie de changer les filtres ou de reformuler ta recherche</p>
                </div>
              ) : (
                <>
                  <div
                    className={`grid gap-3.5 pt-4 transition-opacity ${filterLoading ? "opacity-50" : "opacity-100"}`}
                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
                  >
                    {results.map((hit, i) => (
                      <FormationCard key={hit.objectID || i} hit={hit} onSelect={setSelectedHit} />
                    ))}
                  </div>
                  <div ref={sentinelRef} className="mt-4 flex justify-center py-4">
                    {loadingMore && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <div className="w-4 h-4 rounded-full border-2 border-teal-500/40 border-t-teal-500 animate-spin" />
                        Chargement…
                      </div>
                    )}
                  </div>
                </>
              )
            )}

            {/* État initial */}
            {!hasSearched && !thinking && (
              <div className="pt-6">
                <div className="text-center mb-6">
                  <p className="text-base font-bold text-gray-200 mb-1">Explore les formations</p>
                  <p className="text-sm text-gray-500">Dip fait le reste</p>
                </div>
                <JobSuggestions
                  suggestions={cfg.suggestions}
                  onSelect={(text) => runSearch(text, [])}
                />
              </div>
            )}
          </div>
        </div>

        {/* FILTER PANEL */}
        {showFilters && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={() => setShowFilters(false)}
            />
            <div className="fixed top-0 right-0 h-full z-50 bg-gray-900 w-[260px] shadow-[-4px_0_24px_rgba(0,0,0,0.4)] md:relative md:w-[240px] md:border-l md:border-gray-800 md:z-auto md:shadow-none flex flex-col">
              <FilterPanel
                filterState={filterState}
                facets={facets}
                filterDimensions={cfg.filterDimensions}
                filtersData={null}
                onToggle={handleFilterToggle}
                onReset={handleFilterReset}
                activeCount={activeCount}
                onClose={() => setShowFilters(false)}
              />
            </div>
          </>
        )}
      </div>

      {/* DETAIL PANEL */}
      {selectedHit && (
        <DipDetailPanel
          hit={selectedHit}
          onClose={() => setSelectedHit(null)}
        />
      )}

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => { setShowPremiumModal(false); navigate('/chat/lux'); }}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="DIP est une fonctionnalité exclusive ORIA Premium"
        reason="accéder à DIP"
      />
    </div>
  );
}
